import { lazy, Suspense, useMemo, useRef } from 'react'
import Chart from 'react-apexcharts'
import {
  DEFAULT_TIMEFRAME,
  resolveDisplay,
  type CardChart,
  type ChartCard,
  type Timeframe,
} from '@/features/dashboard/api/schemas'
import { cx } from '@/shared/lib/classNames'
import { ExpandedBarChart } from './ExpandedBarChart'
import { RankBars } from './RankBars'
import { chartSummary } from './lib/chartSummary'
import { deepDiveKind, isCategoryKind } from './lib/deepDiveKind'
import {
  deepDiveModel,
  deepDiveSeries,
  donutSeries,
  type DeepDiveModel,
} from './lib/deepDiveModel'
import { deepDiveOptions } from './lib/deepDiveOptions'
import { useChartPalette } from './lib/useChartPalette'
import { useElementSize } from './lib/useElementSize'
import styles from './DeepDiveChart.module.css'

// The map's outlines are a sizeable chunk of data: loaded when a map is drawn.
const ChoroplethMap = lazy(() =>
  import('./ChoroplethMap').then(module => ({ default: module.ChoroplethMap }))
)

/** The reference series of a ranking ('Previous'), in the order of its labels; none without one. */
const previousValues = (card: ChartCard, labels: string[]) => {
  const reference = card.series.find(series => series.reference)
  if (!reference) return undefined
  return labels.map(label => {
    const index = card.categories.indexOf(label)
    return index < 0 ? null : (reference.data[index] ?? null)
  })
}

type DeepDiveChartProps = {
  card: ChartCard
  /** How the deep dive draws the card (from its stub); the card's own type if absent. */
  chart?: CardChart | null | undefined
  height?: number
  timeframe?: Timeframe | undefined
}

type ApexDeepDiveProps = {
  card: ChartCard
  chart: CardChart | null | undefined
  model: DeepDiveModel
  height: number
}

const ApexDeepDive = ({ card, chart, model, height }: ApexDeepDiveProps) => {
  const { theme, palette } = useChartPalette()
  const kind = deepDiveKind(card, chart)
  const plotRef = useRef<HTMLDivElement>(null)
  const { width } = useElementSize(plotRef)
  const options = useMemo(
    () => deepDiveOptions(card, model, palette, theme, height, width),
    [card, model, palette, theme, height, width]
  )
  const series = useMemo(
    () => (model.kind === 'pie' ? donutSeries(model) : deepDiveSeries(model)),
    [model]
  )

  return (
    <div
      role='img'
      aria-label={chartSummary(card, undefined, chart)}
      className={cx(styles.chart, isCategoryKind(kind) && styles.categories)}
    >
      <div ref={plotRef} className={styles.plot}>
        {kind === 'map' ? (
          <Suspense fallback={<div className={styles.loading} />}>
            <ChoroplethMap card={card} />
          </Suspense>
        ) : (
          <Chart
            type={model.apexType}
            height={height}
            width='100%'
            options={options}
            series={series}
          />
        )}
      </div>
    </div>
  )
}

/** A deep-dive chart, drawn as its `deepDiveKind`; the kinds without a chart of their own are areas. */
export const DeepDiveChart = ({
  card,
  chart,
  height = 340,
  timeframe = DEFAULT_TIMEFRAME,
}: DeepDiveChartProps) => {
  const kind = deepDiveKind(card, chart)
  const model = useMemo(() => deepDiveModel(card, chart), [card, chart])

  // The home chart needs a category and has one y axis; otherwise the generic chart draws it.
  if (kind === 'combo' && card.categories.length > 0 && !model.dual) {
    return (
      <div className={styles.combo} style={{ height }}>
        <ExpandedBarChart card={card} timeframe={timeframe} />
      </div>
    )
  }

  // A ranking is a table of bars, not an Apex chart.
  if (model.kind === 'hbar') {
    return model.labels.length > 0 ? (
      <RankBars
        card={card}
        categories={model.labels}
        values={model.totals}
        previous={previousValues(card, model.labels)}
        lowerIsBetter={resolveDisplay(card).lowerIsBetter}
      />
    ) : null
  }

  return (
    <ApexDeepDive card={card} chart={chart} model={model} height={height} />
  )
}
